import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { User } from './../models/user';
import { Species } from '../models/species';
import { Disease } from '../models/disease';
import { SurgicalOperation } from '../models/surgicalOperation';
import { Animal, AnimalVaccine, AnimalDisease, AnimalSurgicalOperation } from './../models/animal';
import { AgendaEventReportDocument, AgendaEvent, AgendaEventDocument, AgendaEventDocumentType, AgendaEventType, AgendaEventStatut, AgendaEventReport } from './../models/agenda';
import { VaccineLabel } from './../models/vaccine';
import { StorageService } from './storage.service'
import { AlertController } from '@ionic/angular';
import { NetworkService } from './network.service';
import { UserService } from './user.service';
import { DateUtilsService } from './date-utils.service';
import { DiseaseService } from './disease.service';
import { SurgicalOperationService } from './surgical-operation.service';
import { IndexedDbService } from './indexed-db.service';
import { TranslateService } from '@ngx-translate/core';
import { ComptaInvoice, ComptaInvoiceLine } from '../models/compta';
import { ComptaService } from './compta.service';
import { AnimalService } from './animal.service';

@Injectable({
  providedIn: 'root'
})
export class AgendaService {
  events: AgendaEvent[] = [];
  agendaEventDocumentTypes: AgendaEventDocumentType[] = [];
  agendaEventTypes: AgendaEventType[] = [];
  agendaEventStatuts: AgendaEventStatut[] = [];

  constructor(
      private http: HttpClient,
      public storageService: StorageService,
      public networkService: NetworkService,
      private translate: TranslateService,
      private dateUtils: DateUtilsService,
      private diseaseService: DiseaseService,
      private surgicalOperationService: SurgicalOperationService,
      private userService: UserService,
      private comptaService: ComptaService,
      private animalService: AnimalService,
      public indexedDbService: IndexedDbService,
      public alertController: AlertController) { }

      dbSaveEvent(event: AgendaEvent, DoNotSendNotifications: boolean = false) {
        return new Promise((resolve, reject) => {
          if (this.networkService.isOnline()) {
            let headers = new HttpHeaders({
                'Authorization': this.userService.getHttpHeaders()
            });
            let formData: { [key: string]: any } = {};
            formData["id"] = event.id;

            formData["dateTimeStart"] = event.dateTimeStart;
            formData["dateTimeEnd"] = event.dateTimeEnd;
            formData["statutId"] = event.statut?.id ?? '0';
            formData["typeId"] = event.type?.id ?? '0';
            formData["comment"] = event.comment;
            formData["treatmentIsSuccess"] = event.treatmentIsSuccess;
            formData["treatmentDuration"] = event.treatmentDuration;
            formData["treatmentDurationUnit"] = event.treatmentDurationUnit;
            formData["treatmentIsRecurring"] = event.treatmentIsRecurring;
            formData["treatmentIsPonctual"] = event.treatmentIsPonctual;
            formData["createdFromEventId"] = event.createdFromEventId;
            formData["DoNotSendNotifications"] = DoNotSendNotifications;

            formData["animalsId"] = [];
            for(var a of event.animals) {
              formData["animalsId"].push(a.id);
            }
    
            formData['documents'] = []
            for(var document of event.documents) {
              var d: { [key: string]: any } = {
                'id': document.id,
                'file': document.file,
                'base64': document.id == null ? document.base64: null,
                'name': document.name,
                'type': document.type,
                'fileName': document.fileName,
              }
              formData['documents'].push(d)
            }

            this.http.post<any[]>(environment.API_URL + 'agenda/ws_private/UpdateEvent/', formData, {headers : headers}).subscribe({
              next: (response: { [key: string]: any }) => {
                if (event.id == null) {
                  event.id = response['id']
                }
                resolve(event);
              },
              error: async (error) => {
                reject(error);
              }
            });
          } else {
            this.indexedDbService.addItem({'obj': event, 'action': 'save'}).subscribe(() => {});
            resolve(event)
          }
        });
      }

      dbSaveReports(event: AgendaEvent, reports:AgendaEventReport[]) {
        return new Promise((resolve, reject) => {
          if (this.networkService.isOnline()) {
            let headers = new HttpHeaders({
                'Authorization': this.userService.getHttpHeaders()
            });
            let formData: { [key: string]: any } = {'id': event.id, 'reports': []};

            for(let report of reports) {
              var r: { [key: string]: any } = {
                'id': report.id,
                'report': report.report,
                'weight': report.weight,
                'isNothingToReport': report.isNothingToReport,
                'isNoTreatment': report.isNoTreatment,
                'animalId': report.animal?.id ?? 0,
                'diseases': [],
                'surgicalOperations': [],
                'treatment': null,
                'documents': [],
                'vaccines': [],
              }
              formData['reports'].push(r)

              for(let d of report.diseases) {
                r['diseases'].push({'id': d.id, 'date': d.date, 'dateEnd': d.dateEnd, 'isEnd': d.isEnd, 'diseaseId': d.disease?.id ?? null});
              }

              for(let d of report.surgicalOperations) { //
                r['surgicalOperations'].push({'id': d.id, 'date': d.date, 'surgicalOperationId': d.surgicalOperation?.id ?? null});
              }

              if(report.treatment) {
                r['treatment'] = {
                  'id': report.treatment.id,
                  'comment': report.treatment.comment,
                  'dateTimeStart': report.treatment.dateTimeStart,
                  'dateTimeEnd': report.treatment.dateTimeEnd,
                  'typeId': report.treatment.type?.id ?? null,
                  'statutId': report.treatment.statut?.id ?? null,
                  'userId': report.treatment.userId,
                  'animalsId': report.treatment.animals.map(a => a.id),
                  'documents': [],
                }
                for(var document of report.treatment.documents) {
                  r['treatment']['documents'].push({
                    'id': document.id,
                    'file': document.file,
                    'base64': document.id == null ? document.base64: null,
                    'name': document.name,
                    'type': document.type,
                    'fileName': document.fileName,
                  })
                }
              }

              for(var document of report.documents) {
                var d: { [key: string]: any } = {
                  'id': document.id,
                  'file': document.file,
                  'base64': document.id == null ? document.base64: null,
                  'name': document.name,
                  'type': document.type,
                  'fileName': document.fileName,
                }
                r['documents'].push(d)
              }

              for(var vaccine of report.vaccines) {
                var d: { [key: string]: any } = {
                  'id': vaccine.id,
                  'date': vaccine.date,
                  'labelId': vaccine.label?.id,
                }
                r['vaccines'].push(d)
              }
            }
            this.http.post<any[]>(environment.API_URL + 'agenda/ws_private/UpdateReports/', formData, {headers : headers}).subscribe({
              next: (response: { [key: string]: any }) => {
                if (event.id == null) {
                  event.id = response['id']
                }
                for(let i = 0; i < event.reports.filter(a => a.treatment != null).length; i++) {
                  let report = event.reports.filter(a => a.treatment != null)[i]
                  let reportData = response['reports'][i]
                  report.id = reportData['id']
                  if(report.treatment) {
                    report.treatment.id = reportData['treatment']['id']
                  }
                }
                resolve({'event': event, 'allAnimalsHasAReport': response['allAnimalsHasAReport']});
              },
              error: async (error) => {
                reject(error);
              }
            });
          } else {
            this.indexedDbService.addItem({'obj': event, 'action': 'save'}).subscribe(() => {});
            resolve({'event': event, 'allAnimalsHasAReport': undefined})
          }
        });
      }

      dbGetEvent(eventId: number | null): Promise<AgendaEvent | null | undefined> {
        return new Promise((resolve) => {
          if (this.networkService.isOnline()) {
            let headers = new HttpHeaders({
                'Authorization': this.userService.getHttpHeaders()
            });
    
            var url = environment.API_URL + 'agenda/ws_private/' + eventId + '/GetEvent/';
            if(eventId == null)
              url = environment.API_URL + 'agenda/ws_private/GetEventBlank/';
    
            this.http.get<any[]>(url, {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
    
              for(let s of response['documentTypesList']) {
                var d = this.agendaEventDocumentTypes.find(a => a.id == s['id'])
                if(!d){
                  d = new AgendaEventDocumentType()
                  d.id = s['id'];
                  this.agendaEventDocumentTypes.push(d);
                }
                d.name = s['name'];
              }
              for(let s of response['typesList']) {
                let d = this.agendaEventTypes.find(a => a.id == s['id'])
                if(!d){
                  d = new AgendaEventType()
                  d.id = s['id'];
                  d.isVet = s['isVet'];
                  d.isHeat = s['isHeat'];
                  d.isReproductive = s['isReproductive'];
                  d.isTreatment = s['isTreatment'];
                  d.isDate = s['isDate'];
                  d.isEvent = s['isEvent'];
                  this.agendaEventTypes.push(d);
                }
                d.name = s['name'];
              }
              for(let s of response['statutsList']) {
                let d = this.agendaEventStatuts.find(a => a.id == s['id'])
                if(!d){
                  d = new AgendaEventStatut()
                  d.id = s['id'];
                  this.agendaEventStatuts.push(d);
                }
                d.name = s['name'];
              }
              if('diseasesList' in response) {
                for(let s of response['diseasesList']) {
                  let d = this.diseaseService.diseases.find(a => a.id == s['id'])
                  if(!d){
                    d = new Disease()
                    d.id = s['id'];
                    this.diseaseService.diseases.push(d);
                  }
                  d.name = s['name'];
                }
              }
              if('surgicalOperationList' in response) {
                for(let s of response['surgicalOperationList']) {
                  let d = this.surgicalOperationService.surgicalOperations.find(a => a.id == s['id'])
                  if(!d){
                    d = new SurgicalOperation()
                    d.id = s['id'];
                    this.surgicalOperationService.surgicalOperations.push(d);
                  }
                  d.name = s['name'];
                }
              }
              
              var event = this.events.find(a => a.id == eventId)
              if(!event){
                event = new AgendaEvent()
                this.events.push(event);
              }

              event.id = response['id']
              event.userId = response['userId']
              event.dateTimeStart = response['dateTimeStart']
              event.dateTimeEnd = response['dateTimeEnd']
              event.comment = response['comment']
              event.hasEditRights = response['hasEditRights']
              event.treatmentIsSuccess = response['treatmentIsSuccess']
              event.treatmentDuration = response['treatmentDuration']
              event.treatmentDurationUnit = response['treatmentDurationUnit']
              event.treatmentIsRecurring = response['treatmentIsRecurring']
              event.treatmentIsPonctual = response['treatmentIsPonctual']
              event.createdFromEventId = response['createdFromEventId']

              for(let b of response['invoices']) {
                var invoice = this.comptaService.invoices.find(a => a.id == b['id'])
                if(!invoice){
                  invoice = new ComptaInvoice()
                  this.comptaService.invoices.push(invoice);
                }
  
                invoice.id = b['id']
                invoice.amount = b['amount']
                invoice.userId = b['userId']
                invoice.date = b['date']
                invoice.file = b['file']
  
                if(b['event']) {
                  invoice.eventId = b['event']['id']
                  invoice.eventName = b['event']['name']
                  invoice.eventDate = b['event']['date']
                }
  
                for(var line of b['lines']) {
                  var l = invoice.lines.find(b => b.id == line['id'])
                  if(!l){
                    l = new ComptaInvoiceLine()
                    invoice.lines.push(l);
                  }
                  l.id = line['id']
                  l.amount = line['amount']
                  l.description = line['description']
                  l.animals = []
                  for(var animal of line['animals']) {
                    var a = this.animalService.animals.find(b => b.id == animal['id'])
                    if(!a){
                      a = new Animal()
                      a.id = animal['id']
                      a.name = animal['name']
                    }
                    l.animals.push(a)
                  }
                }
              }

              for(var animal of response['animals']) {
                var a = event.animals.find(b => b.id == animal['id'])
                if(!a){
                  a = new Animal()
                  event.animals.push(a);
                }
                a.id = animal['id']
                a.name = animal['name']
                a.avatar = animal['avatar']
                a.avatarBase64 = animal['avatarBase64']
                a.species = new Species()
                a.species.id = animal['species']['id']
                a.species.name = animal['species']['name']
                a.species.hasVaccines = animal['species']['hasVaccines']
              }

              event.documents = []
              for(let d of response['documents']) {
                var document = new AgendaEventDocument()
                document.id = d['id']
                document.file = d['file']
                document.name = d['name']
                document.type = d['type']
                document.fileName = d['file']
                event.documents.push(document);
              }

              event.statut = this.agendaEventStatuts.find(a => a.id == response['statut']['id']) ?? null;
              event.type = this.agendaEventTypes.find(a => a.id == response['type']['id']) ?? null;

              event.reports = []
              if('reports' in response) {
                for(var r of response['reports']) {
                  let report = new AgendaEventReport()
                  report.id = r['id']
                  report.report = r['report']
                  report.isNothingToReport = r['isNothingToReport']
                  report.isNoTreatment = r['isNoTreatment']
                  report.weight = r['weight']
                  report.animal = event.animals.find(a => a.id == r['animalId']) ?? null;
                  for(let d of r['diseases']) {
                    var disease = new AnimalDisease()
                    disease.id = d['id']
                    disease.date = d['date']
                    disease.dateEnd = d['dateEnd']
                    disease.isEnd = d['isEnd']
                    disease.disease = new Disease()
                    disease.disease.id = d['disease']['id']
                    disease.disease.name = d['disease']['name']
                    report.diseases.push(disease);
                  }
                  for(let d of r['surgicalOperations']) {
                    var surgicalOperation = new AnimalSurgicalOperation()
                    surgicalOperation.id = d['id']
                    surgicalOperation.date = d['date']
                    surgicalOperation.surgicalOperation = new SurgicalOperation()
                    surgicalOperation.surgicalOperation.id = d['surgicalOperation']['id']
                    surgicalOperation.surgicalOperation.name = d['surgicalOperation']['name']
                    report.surgicalOperations.push(surgicalOperation);
                  }
                  if(r['treatment']) {
                    report.treatment = this.events.find(a => a.id == r['treatment']['id']) ?? null;
                    if(!report.treatment) {
                      report.treatment = new AgendaEvent()
                      this.events.push(report.treatment);
                    }
                    report.treatment.id = r['treatment']['id']
                    report.treatment.comment = r['treatment']['comment']
                    report.treatment.dateTimeStart = r['treatment']['dateTimeStart']
                    report.treatment.dateTimeEnd = r['treatment']['dateTimeEnd']
                    report.treatment.type = this.agendaEventTypes.find(a => a.id == r['treatment']['type']['id']) ?? null;
                    report.treatment.statut = this.agendaEventStatuts.find(a => a.id == r['treatment']['statut']['id']) ?? null;
                    report.treatment.userId = r['treatment']['userId']
                    report.treatment.animals = []
                    for(let a of r['treatment']['animals']) {
                      let animal = this.animalService.animals.find(b => b.id == a['id']) ?? null;
                      if(!animal) {
                        animal = new Animal()
                        animal.id = a['id']
                        animal.name = a['name']
                        this.animalService.animals.push(animal);
                      }
                      report.treatment.animals.push(animal);
                    }
                  }

                  for(let d of r['documents']) {
                    var document = new AgendaEventReportDocument()
                    document.id = d['id']
                    document.file = d['file']
                    document.name = d['name']
                    document.type = d['type']
                    document.fileName = d['file']
                    report.documents.push(document);
                  }

                  for(let d of r['vaccines']) {
                    var vaccine = new AnimalVaccine()
                    vaccine.id = d['id']
                    vaccine.date = d['date']
                    vaccine.label = new VaccineLabel()
                    vaccine.label.id = d['label']['id']
                    vaccine.label.name = d['label']['name']
                    report.vaccines.push(vaccine);
                  }

                  event.reports.push(report);
                }
              }

              resolve(event);
            },
            () => {
              resolve(this.events.find(a => a.id == eventId));
            })
          } else {
            resolve(this.events.find(a => a.id == eventId));
          }
        });
      }

      dbGetEventsOfUser(userId: number): Promise<AgendaEvent[] | null> {
        return new Promise((resolve) => {
          if (this.networkService.isOnline()) {
            let headers = new HttpHeaders({
                'Authorization': this.userService.getHttpHeaders()
            });
            this.http.get<any[]>(environment.API_URL + 'agenda/ws_private/' + userId + '/GetEventsOfUser/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
              var events: AgendaEvent[] =[]
    
              for(let b of response['events']) {
                var event = this.events.find(a => a.id == b['id'])
                if(!event){
                  event = new AgendaEvent()
                  this.events.push(event);
                }

                event.id = b['id']
                event.userId = b['userId']
                event.dateTimeStart = b['dateTimeStart']
                event.dateTimeEnd = b['dateTimeEnd']
                event.comment = b['comment']
                event.hasEditRights = b['hasEditRights']  
                event.treatmentIsSuccess = b['treatmentIsSuccess']
                event.treatmentDuration = b['treatmentDuration']
                event.treatmentDurationUnit = b['treatmentDurationUnit']
                event.treatmentIsRecurring = b['treatmentIsRecurring']
                event.treatmentIsPonctual = b['treatmentIsPonctual']
                event.createdFromEventId = b['createdFromEventId']

                for(let i of b['invoices']) {
                  var invoice = this.comptaService.invoices.find(a => a.id == i['id'])
                  if(!invoice){
                    invoice = new ComptaInvoice()
                    this.comptaService.invoices.push(invoice);
                  }
    
                  invoice.id = i['id']
                  invoice.amount = i['amount']
                  invoice.userId = i['userId']
                  invoice.date = i['date']
                  invoice.file = i['file']
    
                  if(i['event']) {
                    invoice.eventId = i['event']['id']
                    invoice.eventName = i['event']['name']
                    invoice.eventDate = i['event']['date']
                  }
    
                  invoice.lines = []
                  for(var line of i['lines']) {
                    let l = new ComptaInvoiceLine()
                    invoice.lines.push(l);
                    l.id = line['id']
                    l.amount = line['amount']
                    l.description = line['description']
                    l.animals = []
                    for(var animal of line['animals']) {
                      var a = this.animalService.animals.find(b => b.id == animal['id'])
                      if(!a){
                        a = new Animal()
                        a.id = animal['id']
                        a.name = animal['name']
                      }
                      l.animals.push(a)
                    }
                  }
                }

                for(var animal of b['animals']) {
                  var a = event.animals.find(b => b.id == animal['id'])
                  if(!a){
                    a = new Animal()
                    event.animals.push(a);
                  }
                  a.id = animal['id']
                  a.name = animal['name']
                  a.avatar = animal['avatar']
                  a.avatarBase64 = animal['avatarBase64']
                  a.species = new Species()
                  a.species.id = animal['species']['id']
                  a.species.name = animal['species']['name']
                  a.species.hasVaccines = animal['species']['hasVaccines']
                }

                event.documents = []
                for(let d of b['documents']) {
                  var document = new AgendaEventDocument()
                  document.id = d['id']
                  document.file = d['file']
                  document.name = d['name']
                  document.type = d['type']
                  document.fileName = d['file']
                  event.documents.push(document);
                }

                event.statut = this.agendaEventStatuts.find(a => a.id == b['statut']['id']) ?? null;
                if(!event.statut) {
                  event.statut = new AgendaEventStatut()
                  event.statut.id = b['statut']['id'];
                  event.statut.name = b['statut']['name'];
                  this.agendaEventStatuts.push(event.statut);
                }
                event.type = this.agendaEventTypes.find(a => a.id == b['type']['id']) ?? null;
                if(!event.type) {
                  event.type = new AgendaEventType()
                  event.type.id = b['type']['id'];
                  event.type.isVet = b['type']['isVet'];
                  event.type.isHeat = b['type']['isHeat'];
                  event.type.isReproductive = b['type']['isReproductive'];
                  event.type.isTreatment = b['type']['isTreatment'];
                  event.type.isDate = b['type']['isDate'];
                  event.type.isEvent = b['type']['isEvent'];
                  this.agendaEventTypes.push(event.type);
                }
                event.type.name = b['type']['name'];

                event.reports = []
                for(var r of b['reports']) {
                  let report = new AgendaEventReport()
                  report.id = r['id']
                  report.report = r['report']
                  report.isNothingToReport = r['isNothingToReport']
                  report.isNoTreatment = r['isNoTreatment']
                  report.weight = r['weight']
                  report.animal = event.animals.find(a => a.id == r['animalId']) ?? null;
                  for(let d of r['diseases']) {
                    var disease = new AnimalDisease()
                    disease.id = d['id']
                    disease.date = d['date']
                    disease.dateEnd = d['dateEnd']
                    disease.isEnd = d['isEnd']
                    disease.disease = new Disease()
                    disease.disease.id = d['disease']['id']
                    disease.disease.name = d['disease']['name']
                    report.diseases.push(disease);
                  }
                  for(let d of r['surgicalOperations']) {
                    var surgicalOperation = new AnimalSurgicalOperation()
                    surgicalOperation.id = d['id']
                    surgicalOperation.date = d['date']
                    surgicalOperation.surgicalOperation = new SurgicalOperation()
                    surgicalOperation.surgicalOperation.id = d['surgicalOperation']['id']
                    surgicalOperation.surgicalOperation.name = d['surgicalOperation']['name']
                    report.surgicalOperations.push(surgicalOperation);
                  }
                  if(r['treatment']) {
                    report.treatment = this.events.find(a => a.id == r['treatment']['id']) ?? null;
                    if(!report.treatment) {
                      report.treatment = new AgendaEvent()
                      this.events.push(report.treatment);
                    }
                    report.treatment.id = r['treatment']['id']
                    report.treatment.comment = r['treatment']['comment']
                    report.treatment.dateTimeStart = r['treatment']['dateTimeStart']
                    report.treatment.dateTimeEnd = r['treatment']['dateTimeEnd']
                    report.treatment.type = this.agendaEventTypes.find(a => a.id == r['treatment']['type']['id']) ?? null;
                    report.treatment.statut = this.agendaEventStatuts.find(a => a.id == r['treatment']['statut']['id']) ?? null;
                    report.treatment.userId = r['treatment']['userId']
                    report.treatment.animals = []
                    for(let a of r['treatment']['animals']) {
                      let animal = this.animalService.animals.find(b => b.id == a['id']) ?? null;
                      if(!animal) {
                        animal = new Animal()
                        animal.id = a['id']
                        animal.name = a['name']
                        this.animalService.animals.push(animal);
                      }
                      report.treatment.animals.push(animal);
                    }
                  }

                  for(let d of r['documents']) {
                    var document = new AgendaEventReportDocument()
                    document.id = d['id']
                    document.file = d['file']
                    document.name = d['name']
                    document.type = d['type']
                    document.fileName = d['file']
                    report.documents.push(document);
                  }

                  for(let d of r['vaccines']) {
                    var vaccine = new AnimalVaccine()
                    vaccine.id = d['id']
                    vaccine.date = d['date']
                    vaccine.label = new VaccineLabel()
                    vaccine.label.id = d['label']['id']
                    vaccine.label.name = d['label']['name']
                    report.vaccines.push(vaccine);
                  }

                  event.reports.push(report);
                }

                events.push(event);
              }
    
              resolve(events);
            },
            () => {
              resolve(this.events.filter(a => a.userId = userId));
            })
          } else {
            resolve(this.events.filter(a => a.userId = userId));
          }
        });
      }

      dbDeleteEvent(event: AgendaEvent) {
        return new Promise((resolve, reject) => {
          if (this.networkService.isOnline()) {
            let headers = new HttpHeaders({
                'Authorization': this.userService.getHttpHeaders()
            });
            let formData: { [key: string]: any } = {};
            formData["id"] = event.id;
    
            this.events = this.events.filter(a => a.id != event.id);
            
            this.http.post<any[]>(environment.API_URL + 'agenda/ws_private/DeleteEvent/', formData, {headers : headers}).subscribe({
              next: (response: { [key: string]: any }) => {
                resolve(event);
              },
              error: async (error) => {
                reject(error);
              }
            });
          } else {
            this.indexedDbService.addItem({'obj': event, 'action': 'delete'}).subscribe(() => {});
            resolve(event)
          }
        });
      }

}
